import { t } from '@/features/i18n/utils';
import { useState } from 'react';
import { useAtom } from 'jotai';
import { CancelIcon, Message02Icon } from '@hugeicons-pro/core-stroke-rounded';
import { Icon } from '@/ui/components/icon';
import { chatAnnouncementHiddenAtom } from '@/features/chat/store';
import { Button } from '@/ui/components/button';
import { Card } from '@/ui/components/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogPopup,
  DialogScrollArea,
  DialogTitle,
  DialogTitleBreadcrumb,
  DialogTrigger,
  DialogXClose,
} from '@/ui/components/dialog';
import { Tooltip, TooltipPopup, TooltipTrigger } from '@/ui/components/tooltip';

const CHAT_ANNOUNCEMENT_TITLE_TEXT = {
  analyses: { en: 'Analyses', nb: 'Analyser' },
  boq: { en: 'Bill of quantities', nb: 'Mengdebeskrivelse' },
  drawings: { en: 'Drawings and specs', nb: 'Tegninger og beskrivelser' },
  drafts: { en: 'Bid draft', nb: 'Tilbudsutkast' },
};

const CHAT_ANNOUNCEMENT_DESCRIPTION_TEXT = {
  analyses: {
    en: '“Rerun the cost analysis with the updated quantities.” Chat runs it and updates the analysis.',
    nb: '«Kjør kostnadsanalysen på nytt med de oppdaterte mengdene.» Chat kjører den og oppdaterer analysen.',
  },
  boq: {
    en: '“Draft a BoQ from the tender documents.” You get a first draft to review and refine.',
    nb: '«Lag et utkast til mengdebeskrivelse fra konkurransegrunnlaget.» Du får et første utkast å gå gjennom og bearbeide.',
  },
  drawings: {
    en: '“Check the drawings against the specification.” Chat will list inconsistencies, gaps, and open questions.',
    nb: '«Sjekk tegningene mot beskrivelsen.» Chat lister opp motstrid, mangler og åpne spørsmål.',
  },
  drafts: {
    en: '“Rewrite the method statement in section 3.” Any changes land directly in your draft.',
    nb: '«Skriv om metodebeskrivelsen i kapittel 3.» Endringene legges direkte inn i utkastet ditt.',
  },
};

export function ChatAnnouncementCard() {
  const [hidden, setHidden] = useAtom(chatAnnouncementHiddenAtom);
  const [open, setOpen] = useState(false);

  if (hidden) return null;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Card className="text-sm p-2.5 pt-1.5 relative max-md:hidden">
        <Tooltip>
          <TooltipTrigger
            render={
              <Button
                variant="ghost"
                kind="icon"
                size="xs"
                className="absolute top-1 right-1"
                aria-label={t({ en: 'Hide announcement', nb: 'Skjul kunngjøring' })}
                onClick={() => setHidden(true)}
              >
                <Icon icon={CancelIcon} />
              </Button>
            }
          />
          <TooltipPopup>{t({ en: 'Hide announcement', nb: 'Skjul kunngjøring' })}</TooltipPopup>
        </Tooltip>
        <p className="font-medium mb-2 flex items-center gap-1.5 pr-4">
          <Icon icon={Message02Icon} className="size-4 shrink-0" strokeWidth={2} />
          {t({ en: 'Delegate to chat', nb: 'Deleger til chat' })}
        </p>
        <p className="text-muted mb-2">
          {t({
            en: 'Hand chat a task instead of a question. It drafts, checks, and edits in your project.',
            nb: 'Gi chat en oppgave i stedet for et spørsmål. Den lager utkast, sjekker og redigerer i prosjektet ditt.',
          })}
        </p>
        <DialogTrigger render={<Button variant="tertiary" size="xs" />}>
          {t({ en: 'Learn more', nb: 'Les mer' })}
        </DialogTrigger>
      </Card>
      <DialogPopup size="2xl">
        <DialogTitle>
          <DialogTitleBreadcrumb>
            {t({ en: 'Delegate to chat', nb: 'Deleger til chat' })}
          </DialogTitleBreadcrumb>
          <DialogXClose aria-label={t({ en: 'Close', nb: 'Lukk' })} />
        </DialogTitle>
        <DialogContent>
          <DialogScrollArea viewportClassName="gap-4 text-sm">
            <div>
              <p className="font-medium">
                {t({ en: 'What does this mean?', nb: 'Hva betyr dette?' })}
              </p>
              <DialogDescription className="mt-1 mb-0 max-w-full">
                {t({
                  en: 'Chat can now take on tasks, not just questions. Describe what needs doing and it works in your project, step by step, with you in control.',
                  nb: 'Chat kan nå ta på seg oppgaver, ikke bare spørsmål. Beskriv hva som skal gjøres, så jobber den i prosjektet ditt, steg for steg, med deg ved roret.',
                })}
              </DialogDescription>
            </div>
            <img
              src="/images/chat-tasks.webp"
              alt=""
              className="aspect-10/7 rounded-lg w-full object-cover"
            />
            <div className="space-y-4">
              {(['analyses', 'boq', 'drawings', 'drafts'] as const).map((feature) => (
                <div key={feature}>
                  <p className="font-medium">{t(CHAT_ANNOUNCEMENT_TITLE_TEXT[feature])}</p>
                  <p className="text-muted mt-1">
                    {t(CHAT_ANNOUNCEMENT_DESCRIPTION_TEXT[feature])}
                  </p>
                </div>
              ))}
            </div>
          </DialogScrollArea>
        </DialogContent>
      </DialogPopup>
    </Dialog>
  );
}
